import { Calendar, FileText, Target, Lightbulb, ExternalLink, Video, FolderOpen, Clock, BookOpen, Play, Sparkles } from 'lucide-react';

import { hostLabel, splitReferences } from './topicDescription';

import type { Session, TopicDetail } from '@/lib/api/lms';
import { isRichTextEmpty } from '@/components/RichText';
import { ExpandableProse } from '@/components/ExpandableProse';
import { Player } from '@/features/Visualizer';

function fmtDateTime(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** A Google Calendar "add event" template URL built from session data — no backend needed. */
function calendarUrl(session: Session): string | null {
  if (!session.scheduled_at) return session.meeting_url ?? null;
  const start = new Date(session.scheduled_at);
  if (Number.isNaN(start.getTime())) return session.meeting_url ?? null;
  const end = new Date(start.getTime() + (session.duration_mins ?? 60) * 60_000);
  const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: session.title,
    dates: `${fmt(start)}/${fmt(end)}`,
    ...(session.meeting_url ? { details: `Join: ${session.meeting_url}`, location: session.meeting_url } : {}),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function isLiveNow(session: Session): boolean {
  if (!session.scheduled_at) return false;
  const start = new Date(session.scheduled_at).getTime();
  if (Number.isNaN(start)) return false;
  const end = start + (session.duration_mins ?? 60) * 60_000;
  const now = Date.now();
  return now >= start - 5 * 60_000 && now <= end;
}

function fmtTime(hours?: number): string | null {
  if (!hours || hours <= 0) return null;
  const mins = Math.round(hours * 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

const BADGE = 'flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#ede9fe] text-[#8b5cf6]';

const TYPE_LABEL: Record<string, string> = {
  lecture: 'Concept walkthrough',
  lab: 'Hands-on lab',
  project: 'Milestone project',
};

export function LessonOverview({
  description, upcomingSession, estimatedHours, topicType, sessionCount, resourceCount, assessmentCount,
  visualization, batchId, topicId, pattern, moduleTitle,
}: {
  description?: string;
  /** Technique this topic teaches, shown joined to its module below. */
  pattern?: string;
  moduleTitle?: string;
  upcomingSession?: Session | null;
  estimatedHours?: number;
  topicType: string;
  sessionCount: number;
  resourceCount: number;
  assessmentCount: number;
  visualization?: TopicDetail['visualization'];
  batchId: string;
  topicId: string;
}) {
  // Seeded descriptions end with a bare "Reference: <url>"; lift it out of the
  // prose so it renders as a real link instead of literal text.
  const { html: prose, references } = splitReferences(description);
  const hasDescription = !isRichTextEmpty(prose);
  const time = fmtTime(estimatedHours);

  const stepCount = visualization
    ? visualization.script.variants.reduce((n, v) => n + v.steps.length, 0)
    : 0;
  const approachCount = visualization?.script.variants.length ?? 0;

  // "What's included" — derived from real topic content, an honest stand-in for static objectives.
  const included: string[] = [];
  if (visualization) {
    included.push(
      approachCount > 1
        ? `Interactive walkthrough — ${approachCount} approaches, ${stepCount} steps`
        : `Interactive walkthrough — ${stepCount} steps`,
    );
  }
  if (sessionCount > 0) included.push(sessionCount > 1 ? `${sessionCount} recorded sessions` : 'Recorded session');
  if (resourceCount > 0) included.push(`${resourceCount} learning resource${resourceCount > 1 ? 's' : ''}`);
  if (assessmentCount > 0) included.push(`${assessmentCount} practice ${assessmentCount > 1 ? 'problems' : 'problem'}`);
  if (time) included.push(`~${time} of learning`);
  included.push(TYPE_LABEL[topicType] ?? topicType);

  // Contextual learning tip — only shown when the content makes it true.
  const tip = visualization
    ? 'Play the walkthrough, then try re-deriving the steps yourself before looking at the code.'
    : sessionCount > 0 && assessmentCount > 0
    ? 'Watch the recording first to understand the concept, then try the practice.'
    : assessmentCount > 0
      ? 'Try the practice problems to reinforce what you learn here.'
      : sessionCount > 0
        ? 'Watch the recording, then jot down your takeaways in Notes.'
        : null;

  return (
    <div className="space-y-5">
      {upcomingSession && (
        <div className="flex flex-col gap-4 rounded-2xl border border-[#ececec] bg-white p-5 sm:flex-row sm:items-center">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-[#fff3ee] text-[#ff5a1f]">
            <Calendar className="size-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-[#9ca3af]">Upcoming Live Session</span>
              {isLiveNow(upcomingSession) && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#ef4444] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  <span className="size-1.5 rounded-full bg-white" />Live
                </span>
              )}
            </div>
            <p className="mt-0.5 truncate font-bold text-[#111827]">{upcomingSession.title}</p>
            <p className="mt-0.5 text-sm text-[#6b7280]">
              {fmtDateTime(upcomingSession.scheduled_at)}
              {upcomingSession.trainer_name ? ` • By ${upcomingSession.trainer_name}` : ''}
            </p>
          </div>
          {calendarUrl(upcomingSession) && (
            <a
              href={calendarUrl(upcomingSession)!}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-xl border border-[#8b5cf6] px-4 text-sm font-semibold text-[#8b5cf6] transition-colors hover:bg-[#f5f3ff]"
            >
              View in Calendar<ExternalLink className="size-3.5" />
            </a>
          )}
        </div>
      )}

      {/*
        The walkthrough leads the tab when one exists — it is the best thing on
        the page, so it should not sit below two cards of metadata. Topics
        without a script fall through to the original layout untouched.
      */}
      {visualization && (
        <section aria-label="Interactive walkthrough">
          <div className="mb-2.5 flex items-center gap-2">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#fff3ee] text-[#ff5a1f]">
              <Play className="size-[18px]" />
            </div>
            <h2 className="font-bold text-[#111827]">Watch it work</h2>
          </div>
          <Player visualization={visualization} batchId={batchId} topicId={topicId} />
        </section>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        {/* About This Topic */}
        <section className="rounded-2xl border border-[#ececec] bg-white p-5">
          <div className="flex items-center gap-3">
            <div className={BADGE}><FileText className="size-[18px]" /></div>
            <h2 className="font-bold text-[#111827]">About This Topic</h2>
          </div>

          {/*
            Module and pattern read as one fact — "where this sits in the course"
            and "what technique it is" — rather than two ways to browse the same
            curriculum. The pattern appears here and nowhere else.
          */}
          {(moduleTitle || pattern) && (
            <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] font-medium">
              {moduleTitle && <span className="text-[#6b7280]">{moduleTitle}</span>}
              {moduleTitle && pattern && <span className="text-[#d1d5db]" aria-hidden>·</span>}
              {pattern && (
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#fff3ee] px-2.5 py-1 text-xs font-semibold text-[#ff5a1f]">
                  <Sparkles className="size-3.5" />
                  {pattern}
                </span>
              )}
            </p>
          )}
          <div className="mt-4 text-sm leading-relaxed text-[#374151]">
            {hasDescription
              ? <ExpandableProse html={prose} />
              : <p className="text-[#9ca3af]">No description has been added for this topic yet.</p>}
          </div>
          {references.length > 0 && (
            <div className="mt-4 border-t border-[#f3f4f6] pt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#9ca3af]">Reference</p>
              <ul className="mt-2 flex flex-wrap gap-2">
                {references.map((url) => (
                  <li key={url}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-[#e5e7eb] px-2.5 py-1.5 text-xs font-medium text-[#6b7280] transition-colors hover:border-[#8b5cf6] hover:text-[#8b5cf6]"
                    >
                      {hostLabel(url)}
                      <ExternalLink className="size-3" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* What's Included (honest substitute for static "What You Will Learn") */}
        <section className="rounded-2xl border border-[#ececec] bg-white p-5">
          <div className="flex items-center gap-3">
            <div className={BADGE}><Target className="size-[18px]" /></div>
            <h2 className="font-bold text-[#111827]">What&apos;s Included</h2>
          </div>
          <ul className="mt-4 space-y-2.5 text-sm text-[#374151]">
            {included.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="mt-[7px] size-1.5 shrink-0 rounded-full bg-[#8b5cf6]" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap gap-3 border-t border-[#f3f4f6] pt-4 text-xs font-medium text-[#9ca3af]">
            {sessionCount > 0 && <span className="inline-flex items-center gap-1.5"><Video className="size-3.5" />Recording</span>}
            {resourceCount > 0 && <span className="inline-flex items-center gap-1.5"><FolderOpen className="size-3.5" />Resources</span>}
            {assessmentCount > 0 && <span className="inline-flex items-center gap-1.5"><BookOpen className="size-3.5" />Practice</span>}
            {time && <span className="inline-flex items-center gap-1.5"><Clock className="size-3.5" />{time}</span>}
          </div>
        </section>
      </div>

      {tip && (
        <div className="flex items-start gap-3 rounded-2xl border border-[#ffe4d6] bg-[#fff5f0] p-5">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#fff3ee] text-[#ff5a1f]">
            <Lightbulb className="size-[18px]" />
          </div>
          <p className="pt-1.5 text-sm text-[#374151]">
            <span className="font-bold text-[#ff5a1f]">Learning Tip:</span> {tip}
          </p>
        </div>
      )}
    </div>
  );
}
